import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  blockJobBody, blockKind, blockRunProblem, createWorkbookJobsApi, isFailedState, isTerminal, SearchQueryError,
} from '@bloo-team/api-client'
import type {
  OutlierRecommendation, SearchResultPage, SearchRow, TaskState, WorkbookBlock,
} from '@bloo-team/api-client'
import { client, EXPORT_MAX_ROWS, isDemoSession, searchApiFor } from '@bloo-team/core-shell'

/**
 * Running a workbook's blocks (#205, design B · Tab).
 *
 * Chaining (confirmed with the backend owner, 2026-09-28):
 * - every block after the first carries the PREVIOUS block's task id on invoke;
 * - Text starts no job and passes the previous task id on, so the chain never
 *   breaks; Visualization is not run at all — it charts the block above it
 *   (chartSourceFor) and later blocks read past it;
 * - there is no client check that the previous block has run — the backend
 *   checks; with no previous task id (the first block, or one not run yet) the
 *   block sends `task_id: ""`;
 * - like the legacy console, a Call / Return with no workbook and a Signal with
 *   no Suspect / Target mapping are refused before the request.
 * Visualization reads the previous task's result, unpaginated.
 */

/* ------------------------------------------------------------------ runtime */

export interface BlockRuntime {
  invoke: (q: { clusterId: string; body: Record<string, unknown> }) => Promise<string>
  state: (q: { clusterId: string; scopeId: string; taskId: string }) => Promise<TaskState>
  result: (q: { clusterId: string; scopeId: string; taskId: string; pageNo: number; pageSize: number }) => Promise<SearchResultPage>
  revoke: (q: { clusterId: string; scopeId: string; taskId: string }) => Promise<void>
  recommendations: (q: { clusterId: string; scopeId: string; stream: string }) => Promise<OutlierRecommendation[]>
}

const liveJobs = createWorkbookJobsApi(client)

function liveRuntime(): BlockRuntime {
  const search = searchApiFor(false)
  return {
    invoke: liveJobs.invoke,
    state: search.state,
    result: search.result,
    revoke: search.revoke,
    recommendations: liveJobs.recommendations,
  }
}

/**
 * The demo twin. dql / sql go to core-shell's demo search engine (same task ids,
 * same rows as Fabric Search); every other job kind is a demo task whose rows
 * derive from the previous task's — python passes them through, outlier adds a
 * score, call / return answer a small set, signal / notification pass through.
 */
export function createDemoBlockRuntime(): BlockRuntime {
  const search = searchApiFor(true)
  const tasks = new Map<string, { jobType: string; prev: string | null; polls: number }>()
  let seq = 0

  const derived = async (
    q: { clusterId: string; scopeId: string; taskId: string; pageNo: number; pageSize: number },
    t: { jobType: string; prev: string | null },
  ): Promise<SearchResultPage> => {
    const base: SearchResultPage = t.prev
      ? await runtime.result({ ...q, taskId: t.prev, pageNo: 1, pageSize: 1000 })
      : { rows: [], fields: [], total: 0, executionTime: '0.0100 sec', sourceStreams: [], failed: false, message: null }
    let rows: SearchRow[] = base.rows
    let fields = base.fields
    if (t.jobType === 'ai') {
      rows = rows.slice(0, 5).map((r, i) => ({ ...r, outlier_score: Number((0.92 - i * 0.07).toFixed(2)) }))
      fields = [...fields, 'outlier_score']
    } else if (t.jobType === 'call' || t.jobType === 'return') {
      rows = [{ result: `${t.jobType} returned`, rows: 3 }]
      fields = ['result', 'rows']
    }
    const page = rows.slice((q.pageNo - 1) * q.pageSize, q.pageNo * q.pageSize)
    return { ...base, rows: page, fields, total: rows.length, executionTime: '0.0420 sec', failed: false, message: null }
  }

  const runtime: BlockRuntime = {
    invoke: async ({ clusterId, body }) => {
      const jobType = String(body.job_type ?? '')
      const scopeId = String(body.scope_id ?? '')
      if (jobType === 'dql' || jobType === 'sql') {
        return search.invoke({ clusterId, scopeId, query: String(body.query ?? ''), dialect: jobType })
      }
      seq += 1
      const id = `demo-block-${seq}-id-0`
      tasks.set(id, { jobType, prev: typeof body.task_id === 'string' && body.task_id ? body.task_id : null, polls: 0 })
      return id
    },
    state: async (q) => {
      const t = tasks.get(q.taskId)
      if (!t) return search.state(q)
      t.polls += 1
      return t.polls < 2
        ? { status: 'success', task_state: 'STARTED', task_stage: null }
        : { status: 'success', task_state: 'SUCCESS', task_stage: 'EXECUTED' }
    },
    result: async (q) => {
      const t = tasks.get(q.taskId)
      return t ? derived(q, t) : search.result(q)
    },
    revoke: async (q) => {
      if (!tasks.has(q.taskId)) await search.revoke(q)
    },
    recommendations: async ({ stream }) => [
      {
        entity: '$SrcIP',
        features: [
          { fn: 'distinct', field: '$DstIP', value: null },
          { fn: 'distinct', field: '$DstPort', value: null },
          { fn: 'count', field: '*', value: null },
          ...(stream.toUpperCase() === 'FIREWALL' ? [{ fn: 'percentage_of', field: '$Action', value: 'PACKET_BLOCKED' }] : []),
        ],
      },
      { entity: '$DstIP', features: [{ fn: 'distinct', field: '$SrcIP', value: null }, { fn: 'count', field: '*', value: null }] },
    ],
  }
  return runtime
}

let demoRuntime: BlockRuntime | null = null
let live: BlockRuntime | null = null
export function blockRuntimeFor(isDemo: boolean): BlockRuntime {
  if (isDemo) return (demoRuntime ??= createDemoBlockRuntime())
  return (live ??= liveRuntime())
}

/* ------------------------------------------------------------------ chaining */

export type BlockRunStatus = 'running' | 'done' | 'failed'

export interface BlockRun {
  status: BlockRunStatus
  /** The task holding this block's output — inherited for no-job blocks. */
  taskId: string | null
  /** Whether this block started its own job (false = inherited task id). */
  ownJob: boolean
  error: string | null
}

/** The task id block `i` should send: the nearest earlier run's task id. */
export function prevTaskIdFor(runs: Record<number, BlockRun | undefined>, i: number): string | null {
  for (let j = i - 1; j >= 0; j--) {
    const r = runs[j]
    if (r?.taskId) return r.taskId
  }
  return null
}

/** Kinds that produce or transform data a later block can read. */
export function startsJob(b: WorkbookBlock): boolean {
  const k = blockKind(b)
  return k !== null && k !== 'text' && k !== 'visual'
}

/** Blocks with a Run button and a place in Run all. Visualization has neither: it
 * charts the block above it as soon as that block has a finished task. */
export function isRunnableBlock(b: WorkbookBlock): boolean {
  const k = blockKind(b)
  return k !== null && k !== 'visual'
}

/**
 * The task a Visualization block at `i` charts: the block above it, once that
 * block has finished with a task id. Visualization blocks above are passed over
 * (a chart under a chart reads the same data); an unknown kind stops the walk.
 */
export function chartSourceFor(
  blocks: WorkbookBlock[],
  runs: Record<number, BlockRun | undefined>,
  i: number,
): string | null {
  for (let j = i - 1; j >= 0; j--) {
    const b = blocks[j]
    if (b && blockKind(b) === 'visual') continue
    const r = runs[j]
    return r?.status === 'done' ? r.taskId : null
  }
  return null
}

function queryTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

/** State poll cadence for a running block — Search's own 1s. */
export const BLOCK_POLL_MS = 1000
/**
 * Ceiling on one block's run, measured from invoke (the sql-check harness's
 * TIMEOUT_MS pattern). A task that never reaches a terminal state is revoked —
 * as Cancel does — and the block ends failed with a message, instead of
 * polling for as long as the page stays open. Generous: a Python or outlier
 * block over a wide window is legitimately slow.
 */
export const BLOCK_RUN_TIMEOUT_MS = 10 * 60_000

export interface UseWorkbookRun {
  runs: Record<number, BlockRun | undefined>
  /** Index currently running (single run or the head of run-all). */
  activeIndex: number | null
  runningAll: boolean
  run: (i: number) => void
  runAll: () => void
  cancel: () => void
  /** Forget every result — blocks were reordered, inserted or deleted. */
  reset: () => void
}

/**
 * Runs blocks one at a time, threading task ids. Results are NOT held here —
 * each card reads its own page through `useBlockResult(taskId)`.
 */
export function useWorkbookRun({
  clusterId, scopeId, workbook, blocks, parameters = {}, relativeTime = null,
}: {
  clusterId: string
  scopeId: string
  /** The saved workbook the blocks run in (invoke wbkname / wbkid); null = unsaved. */
  workbook: { id: string; path: string; name: string } | null
  blocks: WorkbookBlock[]
  /** Parameter values by name, as the view bar holds them. */
  parameters?: Record<string, string>
  /** Time travel (`?relative=`); null / absent = off. */
  relativeTime?: number | null
}): UseWorkbookRun {
  const isDemo = isDemoSession()
  const rt = blockRuntimeFor(isDemo)
  const [runs, setRuns] = useState<Record<number, BlockRun | undefined>>({})
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [runningAll, setRunningAll] = useState(false)
  /** Bumped on cancel / reset / unmount — a stale loop sees a new generation and stops. */
  const gen = useRef(0)
  const current = useRef<string | null>(null)
  const runsRef = useRef(runs)
  runsRef.current = runs
  const blocksRef = useRef(blocks)
  blocksRef.current = blocks
  const workbookRef = useRef(workbook)
  workbookRef.current = workbook
  const inputsRef = useRef({ parameters, relativeTime })
  inputsRef.current = { parameters, relativeTime }

  const setRun = (i: number, r: BlockRun) => {
    runsRef.current = { ...runsRef.current, [i]: r }
    setRuns(runsRef.current)
  }

  /** Runs block i to a terminal state; resolves to whether it succeeded. */
  const runOne = useCallback(
    async (i: number, g: number): Promise<boolean> => {
      const b = blocksRef.current[i]
      if (!b) return false
      const prev = prevTaskIdFor(runsRef.current, i)
      const problem = blockRunProblem(b)
      if (problem) {
        setRun(i, { status: 'failed', taskId: null, ownJob: true, error: problem })
        return false
      }
      const body = blockJobBody(b, {
        scopeId,
        prevTaskId: prev,
        queryTimezone: queryTimezone(),
        workbook: workbookRef.current,
        first: i === 0,
        parameters: inputsRef.current.parameters,
        relativeTime: inputsRef.current.relativeTime,
      })
      if (body === null) {
        // Text / Visualization: no job — the chain carries the previous task id.
        setRun(i, { status: 'done', taskId: prev, ownJob: false, error: null })
        return true
      }
      setRun(i, { status: 'running', taskId: null, ownJob: true, error: null })
      let owned: string | null = null
      try {
        const taskId = await rt.invoke({ clusterId, body })
        const started = Date.now()
        owned = taskId
        current.current = taskId
        setRun(i, { status: 'running', taskId, ownJob: true, error: null })
        for (;;) {
          if (gen.current !== g) {
            void rt.revoke({ clusterId, scopeId, taskId }).catch(() => {})
            setRun(i, { status: 'failed', taskId, ownJob: true, error: 'Cancelled.' })
            return false
          }
          const s = await rt.state({ clusterId, scopeId, taskId })
          if (isTerminal(s.task_state)) {
            const failed = isFailedState(s.task_state)
            setRun(i, { status: failed ? 'failed' : 'done', taskId, ownJob: true, error: failed ? 'The block failed to execute.' : null })
            return !failed
          }
          if (Date.now() - started >= BLOCK_RUN_TIMEOUT_MS) {
            void rt.revoke({ clusterId, scopeId, taskId }).catch(() => {})
            setRun(i, {
              status: 'failed', taskId, ownJob: true,
              error: `The block timed out: no result after ${BLOCK_RUN_TIMEOUT_MS / 60_000} minutes, so it was cancelled.`,
            })
            return false
          }
          await sleep(BLOCK_POLL_MS)
        }
      } catch (e) {
        const msg = e instanceof SearchQueryError || e instanceof Error ? e.message : 'The block could not start.'
        setRun(i, { status: 'failed', taskId: null, ownJob: true, error: msg })
        return false
      } finally {
        // Only the run that owns `current` clears it: a stale loop finishing
        // after a newer run took over must not hide that run's task from Cancel.
        if (owned !== null && current.current === owned) current.current = null
      }
    },
    [rt, clusterId, scopeId],
  )

  const run = useCallback(
    (i: number) => {
      const g = ++gen.current
      setRunningAll(false)
      setActiveIndex(i)
      void runOne(i, g).finally(() => {
        if (gen.current === g) setActiveIndex(null)
      })
    },
    [runOne],
  )

  const runAll = useCallback(() => {
    const g = ++gen.current
    runsRef.current = {}
    setRuns({})
    setRunningAll(true)
    void (async () => {
      for (let i = 0; i < blocksRef.current.length; i++) {
        if (gen.current !== g) return
        const b = blocksRef.current[i]
        if (!b || !isRunnableBlock(b)) continue
        setActiveIndex(i)
        const ok = await runOne(i, g)
        if (!ok) break // a failed block stops the chain: later blocks would read nothing
      }
      if (gen.current === g) {
        setActiveIndex(null)
        setRunningAll(false)
      }
    })()
  }, [runOne])

  const cancel = useCallback(() => {
    gen.current += 1
    const t = current.current
    if (t) void rt.revoke({ clusterId, scopeId, taskId: t }).catch(() => {})
    setActiveIndex(null)
    setRunningAll(false)
  }, [rt, clusterId, scopeId])

  const reset = useCallback(() => {
    gen.current += 1
    runsRef.current = {}
    setRuns({})
    setActiveIndex(null)
    setRunningAll(false)
  }, [])

  useEffect(
    () => () => {
      gen.current += 1
    },
    [],
  )

  return { runs, activeIndex, runningAll, run, runAll, cancel, reset }
}

/* ------------------------------------------------------------------ results */

/** Unpaginated reads (Visualization) are capped: a chart of more is not a chart. */
export const VISUAL_MAX_ROWS = 10_000

/**
 * One block's result page. `all` reads the whole result in one page (the
 * endpoint returns 10 rows without paging params — captured 2026-09-28 — so
 * "unpaginated" means asking for total_count).
 */
export function useBlockResult({
  clusterId, scopeId, taskId, pageNo = 1, pageSize = 50, all = false,
}: {
  clusterId: string
  scopeId: string
  taskId: string | null
  pageNo?: number
  pageSize?: number
  all?: boolean
}) {
  const isDemo = isDemoSession()
  const rt = blockRuntimeFor(isDemo)
  return useQuery<SearchResultPage>({
    queryKey: ['block-result', clusterId, scopeId, taskId, all ? 'all' : `${pageNo}:${pageSize}`, isDemo],
    queryFn: async () => {
      const id = taskId as string
      if (!all) return rt.result({ clusterId, scopeId, taskId: id, pageNo, pageSize })
      const first = await rt.result({ clusterId, scopeId, taskId: id, pageNo: 1, pageSize: 1 })
      if (first.failed || first.total <= 1) return first
      return rt.result({ clusterId, scopeId, taskId: id, pageNo: 1, pageSize: Math.min(first.total, VISUAL_MAX_ROWS) })
    },
    enabled: taskId !== null,
    // A finished task's stored result never changes.
    staleTime: Infinity,
    retry: 1,
  })
}

const EXPORT_PAGE_SIZE = 1000

/**
 * A block's whole stored result, for csv/json export — re-read in pages, as
 * Search's exportAll does, and capped at the same EXPORT_MAX_ROWS. Paging a
 * stored result never re-runs the block. Null when the result failed.
 */
export async function exportBlockResult(
  rt: Pick<BlockRuntime, 'result'>,
  { clusterId, scopeId, taskId }: { clusterId: string; scopeId: string; taskId: string },
): Promise<{ fields: string[]; rows: SearchRow[]; truncated: boolean } | null> {
  const first = await rt.result({ clusterId, scopeId, taskId, pageNo: 1, pageSize: EXPORT_PAGE_SIZE })
  if (first.failed) return null
  const rows = [...first.rows]
  for (let no = 2; rows.length < Math.min(first.total, EXPORT_MAX_ROWS); no++) {
    const next = await rt.result({ clusterId, scopeId, taskId, pageNo: no, pageSize: EXPORT_PAGE_SIZE })
    if (next.failed || next.rows.length === 0) break
    rows.push(...next.rows)
  }
  return { fields: first.fields, rows: rows.slice(0, EXPORT_MAX_ROWS), truncated: first.total > EXPORT_MAX_ROWS }
}

/** Outlier feature suggestions for a stream. */
export function useOutlierRecommendations({
  clusterId, scopeId, stream, enabled,
}: {
  clusterId: string
  scopeId: string
  stream: string
  enabled: boolean
}) {
  const isDemo = isDemoSession()
  const rt = blockRuntimeFor(isDemo)
  return useQuery<OutlierRecommendation[]>({
    queryKey: ['outlier-recommendations', clusterId, scopeId, stream, isDemo],
    queryFn: () => rt.recommendations({ clusterId, scopeId, stream }),
    enabled: enabled && stream.trim() !== '',
    staleTime: 5 * 60_000,
  })
}
